import { getSystem } from "../data/loadSystems";
import { setDevDebug, state, type Screen } from "./state";
import { formatPlayerName, goToChangeNickname } from "./playerName";
import type { ScreenContext } from "./app";

export function formatSeconds(totalSeconds: number): string {
  // A 0-DPS ship (a Digger fighting solo, 16.23) makes TTK mathematically
  // infinite -- Infinity % 60 is NaN in JS, so this needs an explicit case
  // rather than falling through to the normal minutes/seconds split.
  if (!Number.isFinite(totalSeconds)) {
    return "∞"; // ∞
  }
  const s = Math.round(totalSeconds);
  if (s < 60) {
    return `${s}s`;
  }
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}m ${seconds}s`;
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) {
    return "∞";
  }
  return value.toFixed(0);
}

export function bandClass(band: string): string {
  switch (band) {
    case "Easy":
      return "band-easy";
    case "Challenge":
      return "band-challenge";
    case "Very Hard":
      return "band-veryhard";
    default:
      return "band-impossible";
  }
}

// The persistent header shown on every pane screen (Mission Board/Diary/
// Cargo/Market/Shipyard/Fleet/Lounge/Ship Card/Options) -- current ship,
// current system, credits, and waypoint. Ship Select intentionally omits
// it (nothing meaningful to show before a ship's picked). Log Out
// deliberately lives only on the Main Menu (mainMenuScreen.ts's own
// signed-in state) -- Don's direction: no reason to repeat it on every
// screen, it's a rare enough action to belong in exactly one place.
//
// Screen-to-screen navigation used to live here too (the "numerous
// buttons" Don flagged as dev-only, since a real captain travels by
// actually flying, not by teleporting between UIs) -- that whole button
// row moved to renderDevNavBar below, visible only behind the DEV DEBUG
// toggle. The one exception is the instant "Travel" shortcut next to the
// Waypoint display: that's also a dev-only skip-the-flight shortcut, so
// it's gated on state.devDebugOn right here instead of moving out.
// Screens that spend credits without navigating away (Base/Station View's
// Repair Bay, Armory, and lot Build all re-render only their own panel)
// left the header showing a pre-purchase balance until the next full
// render -- which reads as "I paid and nothing happened." Cheaper than
// forcing a whole-screen rerender, which would also close the open modal.
export function refreshHeaderCredits(): void {
  const el = document.querySelector<HTMLElement>("[data-header-credits]");
  if (el) el.textContent = `${state.credits.toLocaleString()} CR`;
}

export function renderHeader(container: HTMLElement, ctx: ScreenContext): void {
  const header = document.createElement("div");
  header.className = "app-header";

  const system = getSystem(state.currentSystemId);
  const shipLabel = state.currentShip
    ? `${state.currentShip.name} (${state.currentShip.variant})`
    : "No ship selected";
  const waypoint = state.waypointSystemId ? getSystem(state.waypointSystemId) : null;
  const name = formatPlayerName(state.displayName ?? state.username);

  header.innerHTML = `
    <div class="app-header-info">
      <div><strong>${shipLabel}</strong></div>
      <div class="dim">${system ? `${system.name} &middot; Danger Tier ${system.dangerTier}` : state.currentSystemId}</div>
    </div>
    <div class="app-header-info">
      <div><strong data-header-credits>${state.credits.toLocaleString()} CR</strong></div>
      ${
        waypoint
          ? `<div class="dim">Waypoint: ${waypoint.name} ${
              state.devDebugOn ? '<button type="button" data-travel>DEV: Travel</button>' : ""
            }</div>`
          : ""
      }
    </div>
    <div class="app-header-info">
      <span class="dim">${state.username ? name.text : ""}</span>
      ${name.opaque ? '<button type="button" class="hud-changename" data-change-name>Change Name</button>' : ""}
    </div>
  `;

  header.querySelector<HTMLButtonElement>("[data-change-name]")?.addEventListener("click", goToChangeNickname);

  const travelBtn = header.querySelector<HTMLButtonElement>("button[data-travel]");
  travelBtn?.addEventListener("click", () => {
    if (state.waypointSystemId) {
      state.currentSystemId = state.waypointSystemId;
      state.waypointSystemId = null;
    }
    ctx.rerender();
  });

  container.appendChild(header);

  if (state.saveNotice) {
    const notice = document.createElement("div");
    notice.className = "event-banner";
    notice.innerHTML = `${state.saveNotice} <button type="button" data-dismiss-notice>&times;</button>`;
    notice.querySelector("button")!.addEventListener("click", () => {
      state.saveNotice = null;
      ctx.rerender();
    });
    container.appendChild(notice);
  }

  if (state.lastEvent) {
    const banner = document.createElement("div");
    banner.className = "event-banner";
    banner.innerHTML = `${state.lastEvent} <button type="button" data-dismiss-event>&times;</button>`;
    banner.querySelector("button")!.addEventListener("click", () => {
      state.lastEvent = null;
      ctx.rerender();
    });
    container.appendChild(banner);
  }
}

// Global DEV DEBUG bar (Don's direction): a real captain reaches every
// other screen by physically being somewhere -- Cargo/Fleet/Market/etc.
// from inside one of the three top-level Views (Expedition/Base/Station),
// Base/Station View themselves only by actually docking. Direct-teleport
// navigation between every screen is a dev/testing convenience only, kept
// behind this toggle rather than removed outright. Rendered once, fixed
// to the viewport, on every screen (including Expedition View/Space Map,
// which don't use renderHeader) -- called directly from app.ts's render(),
// before the per-screen content. state.devDebugOn persists across reloads
// since 16.69 (src/settings.ts), so this opens in whatever state it was
// last left in rather than always starting off.
export function renderDevNavBar(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  wrap.className = "dev-debug-wrap";
  wrap.innerHTML = `
    <button type="button" class="dev-debug-toggle${state.devDebugOn ? " on" : ""}" data-dev-toggle>
      <span class="dev-debug-switch"></span>
      DEV DEBUG
    </button>
    <div class="dev-nav-bar${state.devDebugOn ? " open" : ""}">
      <button type="button" data-nav="ships">Change Ship</button>
      <button type="button" data-nav="board">Mission Board</button>
      <button type="button" data-nav="diary">Mission Diary${
        state.acceptedMissions.length > 0 ? ` (${state.acceptedMissions.length})` : ""
      }</button>
      <button type="button" data-nav="expedition" ${state.currentShip ? "" : "disabled"}>Expedition View</button>
      <button type="button" data-nav="spaceMap">Space Map</button>
      <button type="button" data-nav="cargo" ${state.currentShip ? "" : "disabled"}>Cargo</button>
      <button type="button" data-nav="shipyard">Shipyard</button>
      <button type="button" data-nav="fleet">Fleet${state.fleet.length > 0 ? ` (${state.fleet.length})` : ""}</button>
      <button type="button" data-nav="lounge">Captain's Lounge</button>
      <button type="button" data-nav="market" ${state.currentShip ? "" : "disabled"}>Market</button>
      <button type="button" data-nav="base">Base View</button>
      <button type="button" data-nav="station">Station View</button>
    </div>
  `;

  wrap.querySelector<HTMLButtonElement>("[data-dev-toggle]")!.addEventListener("click", () => {
    setDevDebug(!state.devDebugOn);
    ctx.rerender();
  });

  wrap.querySelectorAll<HTMLButtonElement>("button[data-nav]").forEach((btn) => {
    btn.addEventListener("click", () => {
      ctx.navigate(btn.dataset.nav as Screen);
    });
  });

  container.appendChild(wrap);
}
